import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { supabase } from './supabase';
import {
  defaultGenerateAudio,
  getVideoModelById,
  pickVideoModelSelection,
} from './videoModelCatalog';
import type { VideoModelPickerValue } from '../components/video/VideoModelPicker';

export const VIDEO_FEATURES = [
  'staging',
  'flight_plan',
  'ai_insertion',
  'v2v',
  'v2v_recolor',
] as const;

export type VideoFeature = (typeof VIDEO_FEATURES)[number];

/** Static fallback when no usage data yet (or recommended model invalid for modality). */
export const FEATURE_FALLBACK_MODEL: Record<VideoFeature, string> = {
  staging: 'poyo-seedance-mini-480',
  flight_plan: 'poyo-seedance-mini-480',
  ai_insertion: 'poyo-seedance-mini-480',
  v2v: 'kling-omni-v2v',
  v2v_recolor: 'kling-omni-v2v',
};

export type FeatureRecommendation = {
  videoModelId: string;
  useCount: number;
};

type RecMap = Partial<Record<VideoFeature, FeatureRecommendation>>;

let cachedRecs: RecMap | null = null;
let inflight: Promise<RecMap> | null = null;

export async function fetchFeatureRecommendations(force = false): Promise<RecMap> {
  if (!force && cachedRecs) return cachedRecs;
  if (!force && inflight) return inflight;

  inflight = (async () => {
    const { data, error } = await supabase.rpc('get_video_feature_recommended_models');
    if (error) throw error;
    const map: RecMap = {};
    for (const row of data || []) {
      const feature = row.feature as VideoFeature;
      if (!(VIDEO_FEATURES as readonly string[]).includes(feature)) continue;
      map[feature] = {
        videoModelId: String(row.video_model_id),
        useCount: Number(row.use_count) || 0,
      };
    }
    cachedRecs = map;
    return map;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function resolveRecommendedModelId(
  feature: VideoFeature,
  modality: 't2v' | 'i2v' | 'v2v',
  recs?: RecMap | null,
): string {
  const candidate = recs?.[feature]?.videoModelId;
  if (candidate) {
    const model = getVideoModelById(candidate);
    if (model?.modalities.includes(modality)) return candidate;
  }
  return FEATURE_FALLBACK_MODEL[feature];
}

export function pickerValueFromModel(
  modality: 't2v' | 'i2v' | 'v2v',
  videoModelId: string,
  durationSeconds = 5,
): VideoModelPickerValue {
  const sel = pickVideoModelSelection({ videoModelId, modality });
  return {
    videoModelId: sel.videoModelId,
    resolutionId: sel.resolutionId,
    durationSeconds,
    generateAudio: defaultGenerateAudio(sel.model, sel.resolution),
  };
}

/**
 * Recommended (most-used) model id for a feature across all users.
 * Falls back to FEATURE_FALLBACK_MODEL while loading / on error.
 */
export function useRecommendedVideoModel(
  feature: VideoFeature,
  modality: 't2v' | 'i2v' | 'v2v',
): { recommendedModelId: string; useCount: number; ready: boolean } {
  const fallback = FEATURE_FALLBACK_MODEL[feature];
  const [recommendedModelId, setRecommendedModelId] = useState(fallback);
  const [useCount, setUseCount] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchFeatureRecommendations()
      .then((map) => {
        if (cancelled) return;
        const id = resolveRecommendedModelId(feature, modality, map);
        setRecommendedModelId(id);
        setUseCount(map[feature]?.useCount ?? 0);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setRecommendedModelId(fallback);
        setUseCount(0);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [feature, modality, fallback]);

  return { recommendedModelId, useCount, ready };
}

/**
 * Keep pick in sync with community default until the user changes it.
 */
export function useFeatureVideoPick(
  feature: VideoFeature,
  modality: 't2v' | 'i2v' | 'v2v',
  initialDuration = 5,
): {
  value: VideoModelPickerValue;
  setValue: Dispatch<SetStateAction<VideoModelPickerValue>>;
  recommendedModelId: string;
  useCount: number;
} {
  const { recommendedModelId, useCount, ready } = useRecommendedVideoModel(feature, modality);
  const [value, setValue] = useState<VideoModelPickerValue>(() =>
    pickerValueFromModel(modality, FEATURE_FALLBACK_MODEL[feature], initialDuration),
  );
  const [userTouched, setUserTouched] = useState(false);

  useEffect(() => {
    if (!ready || userTouched) return;
    setValue((prev) => {
      if (prev.videoModelId === recommendedModelId) return prev;
      return pickerValueFromModel(modality, recommendedModelId, prev.durationSeconds);
    });
  }, [ready, recommendedModelId, modality, userTouched]);

  const setValueTracked: Dispatch<SetStateAction<VideoModelPickerValue>> = (next) => {
    setValue((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved.videoModelId !== prev.videoModelId) {
        setUserTouched(true);
      }
      return resolved;
    });
  };

  return { value, setValue: setValueTracked, recommendedModelId, useCount };
}
